import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import { revokePendingForBlock } from './slideImagePendingUpload';

/** 레이아웃에 등장하는 모든 블럭 id (퀴즈 세트·보기 연관 포함) */
export function collectLayoutAllEntityIds(layout: ProgramSlideLayoutV1): Set<string> {
  const s = new Set<string>();
  for (const b of layout.boxes ?? []) s.add(b.id);
  for (const b of layout.images ?? []) s.add(b.id);
  for (const b of layout.clipboardImages ?? []) s.add(b.id);
  for (const b of layout.scanImages ?? []) s.add(b.id);
  for (const b of layout.buttons ?? []) s.add(b.id);
  for (const b of layout.shapes ?? []) s.add(b.id);
  for (const b of layout.polylines ?? []) s.add(b.id);
  for (const b of layout.videos ?? []) s.add(b.id);
  for (const b of layout.webpages ?? []) s.add(b.id);
  for (const b of layout.quizzes ?? []) s.add(b.id);
  for (const b of layout.quizChoiceSets ?? []) s.add(b.id);
  for (const b of layout.quizChoiceOptions ?? []) s.add(b.id);
  return s;
}

/** `knownIds`에 없는 블럭만 남김(웹캠 세션 중 추가분만 오버레이 캔버스에 표시) */
export function pruneLayoutToUnknownEntityIds(layout: ProgramSlideLayoutV1, knownIds: Set<string>): ProgramSlideLayoutV1 {
  const optionsNew = (layout.quizChoiceOptions ?? []).filter((o) => !knownIds.has(o.id));
  const setIdsNeeded = new Set(optionsNew.map((o) => o.setId));
  const quizChoiceSets = (layout.quizChoiceSets ?? []).filter((s) => {
    if (!knownIds.has(s.id)) return true;
    return setIdsNeeded.has(s.id);
  });
  const setIds = new Set(quizChoiceSets.map((s) => s.id));
  const quizChoiceOptions = optionsNew.filter((o) => setIds.has(o.setId));
  return {
    ...layout,
    boxes: (layout.boxes ?? []).filter((b) => !knownIds.has(b.id)),
    images: (layout.images ?? []).filter((b) => !knownIds.has(b.id)),
    clipboardImages: (layout.clipboardImages ?? []).filter((b) => !knownIds.has(b.id)),
    scanImages: (layout.scanImages ?? []).filter((b) => !knownIds.has(b.id)),
    buttons: (layout.buttons ?? []).filter((b) => !knownIds.has(b.id)),
    shapes: (layout.shapes ?? []).filter((b) => !knownIds.has(b.id)),
    polylines: (layout.polylines ?? []).filter((b) => !knownIds.has(b.id)),
    videos: (layout.videos ?? []).filter((b) => !knownIds.has(b.id)),
    webpages: (layout.webpages ?? []).filter((b) => !knownIds.has(b.id)),
    quizzes: (layout.quizzes ?? []).filter((b) => !knownIds.has(b.id)),
    quizChoiceSets,
    quizChoiceOptions,
  };
}

function mergeById<T extends { id: string }>(base: T[], incoming: T[]): T[] {
  const incomingIds = new Set(incoming.map((x) => x.id));
  const kept = base.filter((b) => !incomingIds.has(b.id));
  return [...kept, ...incoming];
}

/** 세션 베이스라인에 맞는 블럭 + 오버레이 캔버스에서 온 블럭을 합친 전체 레이아웃 */
export function mergeWebcamSessionLayouts(
  baselineIds: Set<string>,
  previousFull: ProgramSlideLayoutV1,
  ephemeralFromOverlay: ProgramSlideLayoutV1
): ProgramSlideLayoutV1 {
  const basePart = pruneLayoutToKnownEntityIds(previousFull, baselineIds);
  return {
    ...previousFull,
    ...ephemeralFromOverlay,
    v: 1,
    appliedLayoutTemplateId:
      (ephemeralFromOverlay.appliedLayoutTemplateId ?? '').trim() ||
      (previousFull.appliedLayoutTemplateId ?? '').trim() ||
      undefined,
    defaultTextColorHex:
      (ephemeralFromOverlay.defaultTextColorHex ?? '').trim() ||
      (previousFull.defaultTextColorHex ?? '').trim() ||
      undefined,
    defaultBackgroundColorHex:
      (ephemeralFromOverlay.defaultBackgroundColorHex ?? '').trim() ||
      (previousFull.defaultBackgroundColorHex ?? '').trim() ||
      undefined,
    pageBackgroundColorHex:
      (ephemeralFromOverlay.pageBackgroundColorHex ?? '').trim() ||
      (previousFull.pageBackgroundColorHex ?? '').trim() ||
      undefined,
    pageTextColorHex:
      (ephemeralFromOverlay.pageTextColorHex ?? '').trim() ||
      (previousFull.pageTextColorHex ?? '').trim() ||
      undefined,
    boxes: mergeById(basePart.boxes ?? [], ephemeralFromOverlay.boxes ?? []),
    images: mergeById(basePart.images ?? [], ephemeralFromOverlay.images ?? []),
    clipboardImages: mergeById(basePart.clipboardImages ?? [], ephemeralFromOverlay.clipboardImages ?? []),
    scanImages: mergeById(basePart.scanImages ?? [], ephemeralFromOverlay.scanImages ?? []),
    buttons: mergeById(basePart.buttons ?? [], ephemeralFromOverlay.buttons ?? []),
    shapes: mergeById(basePart.shapes ?? [], ephemeralFromOverlay.shapes ?? []),
    polylines: mergeById(basePart.polylines ?? [], ephemeralFromOverlay.polylines ?? []),
    videos: mergeById(basePart.videos ?? [], ephemeralFromOverlay.videos ?? []),
    webpages: mergeById(basePart.webpages ?? [], ephemeralFromOverlay.webpages ?? []),
    quizzes: mergeById(basePart.quizzes ?? [], ephemeralFromOverlay.quizzes ?? []),
    quizChoiceSets: mergeById(basePart.quizChoiceSets ?? [], ephemeralFromOverlay.quizChoiceSets ?? []),
    quizChoiceOptions: mergeById(basePart.quizChoiceOptions ?? [], ephemeralFromOverlay.quizChoiceOptions ?? []),
  };
}

/** `knownIds`에 없는 블럭은 제거(웹캠 미리보기 세션 중 추가된 임시 개체 제거용) */
export function pruneLayoutToKnownEntityIds(layout: ProgramSlideLayoutV1, knownIds: Set<string>): ProgramSlideLayoutV1 {
  const quizChoiceSets = (layout.quizChoiceSets ?? []).filter((x) => knownIds.has(x.id));
  const setIds = new Set(quizChoiceSets.map((x) => x.id));
  const quizChoiceOptions = (layout.quizChoiceOptions ?? []).filter((o) => knownIds.has(o.id) && setIds.has(o.setId));
  return {
    ...layout,
    boxes: (layout.boxes ?? []).filter((b) => knownIds.has(b.id)),
    images: (layout.images ?? []).filter((b) => knownIds.has(b.id)),
    clipboardImages: (layout.clipboardImages ?? []).filter((b) => knownIds.has(b.id)),
    scanImages: (layout.scanImages ?? []).filter((b) => knownIds.has(b.id)),
    buttons: (layout.buttons ?? []).filter((b) => knownIds.has(b.id)),
    shapes: (layout.shapes ?? []).filter((b) => knownIds.has(b.id)),
    polylines: (layout.polylines ?? []).filter((b) => knownIds.has(b.id)),
    videos: (layout.videos ?? []).filter((b) => knownIds.has(b.id)),
    webpages: (layout.webpages ?? []).filter((b) => knownIds.has(b.id)),
    quizzes: (layout.quizzes ?? []).filter((b) => knownIds.has(b.id)),
    quizChoiceSets,
    quizChoiceOptions,
  };
}

export type WebcamFreezeBaselineByPage = Record<number, Set<string>>;

export function captureWebcamFreezeBaselineFromLayouts(
  layouts: Record<number, ProgramSlideLayoutV1>
): WebcamFreezeBaselineByPage {
  const out: WebcamFreezeBaselineByPage = {};
  for (const [idxRaw, lay] of Object.entries(layouts)) {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || !lay) continue;
    out[idx] = collectLayoutAllEntityIds(lay);
  }
  return out;
}

/** 베이스라인에 있던 페이지만 정리. 베이스라인에 없던 페이지(웹캠 켠 뒤 새로 생긴 슬라이드 등)는 그대로 둔다. */
export function pruneLayoutsToWebcamFreezeBaseline(
  layouts: Record<number, ProgramSlideLayoutV1>,
  baseline: WebcamFreezeBaselineByPage | null
): Record<number, ProgramSlideLayoutV1> {
  if (!baseline) return layouts;
  const next: Record<number, ProgramSlideLayoutV1> = { ...layouts };
  for (const [idxRaw, lay] of Object.entries(layouts)) {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || !lay) continue;
    const known = baseline[idx];
    if (!known) continue;
    const beforeIds = collectLayoutAllEntityIds(lay);
    beforeIds.forEach((id) => {
      if (!known.has(id)) revokePendingForBlock(id);
    });
    next[idx] = pruneLayoutToKnownEntityIds(lay, known);
  }
  return next;
}
